import React, { useState, useEffect } from 'react';

const API_URL = '';

const getToken = () => localStorage.getItem('token');

export default function FeaturedProductsManager() {
  const [selectedType, setSelectedType] = useState('all');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const LIMIT = 24;
  const TYPES = ['all', 'album', 'novel', 'art', 'tshirt', 'cap', 'hoodie', 'pdfprint'];

  // Helper function to safely get image URL - THIS FIXES THE ERROR
  const getProductImage = (product) => {
    if (!product) return '/images/placeholder.jpg';
    
    // Check different possible image locations
    if (product.image && typeof product.image === 'string') {
      return product.image;
    }
    if (product.images?.full && typeof product.images.full === 'string') {
      return product.images.full;
    }
    if (product.images?.modal && typeof product.images.modal === 'string') {
      return product.images.modal;
    }
    
    return '/images/placeholder.jpg';
  };

  useEffect(() => {
    fetchProducts(1, true);
  }, []);

  const fetchProducts = async (pageNum = 1, replace = false, type = selectedType) => {
    replace ? setLoading(true) : setLoadingMore(true);
    try {
      const typeParam = type !== 'all' ? `&type=${type}` : '';
      const response = await fetch(`${API_URL}/api/products?limit=${LIMIT}&page=${pageNum}${typeParam}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` },
      });
      const data = await response.json();
      const incoming = Array.isArray(data) ? data : (data.products ?? []);
      setProducts(prev => replace ? incoming : [...prev, ...incoming]);
      setHasMore(data.hasMore ?? false);
      setPage(pageNum);
    } catch (err) {
      setError('Failed to load products');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMore = () => fetchProducts(page + 1, false, selectedType);

  const handleTypeChange = (type) => {
    setSelectedType(type);
    setProducts([]);
    fetchProducts(1, true, type);
  };

  const toggleFeatured = async (productId, currentFeaturedStatus) => {
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${API_URL}/api/products/${productId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`,
        },
        body: JSON.stringify({
          featured: !currentFeaturedStatus
        })
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || `HTTP ${response.status}`);
      }

      setProducts(products.map(p =>
        p.id === productId
          ? { ...p, featured: !currentFeaturedStatus }
          : p
      ));

      setSuccess(`Product ${!currentFeaturedStatus ? 'added to' : 'removed from'} featured section`);
      setTimeout(() => setSuccess(''), 3000);

    } catch (err) {
      console.error('Error updating product:', err);
      setError(`Failed to update product: ${err.message}`);
    }
  };

  if (loading) {
    return <div style={styles.loading}>Loading products...</div>;
  }

  const featuredProducts = products.filter(p => p.featured);
  const nonFeaturedProducts = products.filter(p => !p.featured);

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>Manage Featured Products</h2>
      
      <div style={styles.filterBar}>
        {TYPES.map(type => (
          <button
            key={type}
            onClick={() => handleTypeChange(type)}
            style={{
              ...styles.filterButton,
              ...(selectedType === type ? styles.filterButtonActive : {})
            }}
          >
            {type.charAt(0).toUpperCase() + type.slice(1)}
          </button>
        ))}
      </div>
      
      <p style={styles.subtitle}>
        Select which products appear in the Featured section on the homepage
      </p>

      {error && <div style={styles.error}>{error}</div>}
      {success && <div style={styles.success}>{success}</div>}

      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>
          Currently Featured ({featuredProducts.length})
        </h3>
        <div style={styles.productGrid}>
          {featuredProducts.length === 0 ? (
            <p style={styles.emptyMessage}>No featured products yet</p>
          ) : (
            featuredProducts.map(product => (
              <div key={product.id} style={styles.productCard}>
                <img
                  src={getProductImage(product)}
                  alt={product.name || 'Product'}
                  style={styles.productImage}
                  onError={(e) => {
                    e.target.src = '/images/placeholder.jpg';
                  }}
                />
                <div style={styles.productInfo}>
                  <h4 style={styles.productName}>{product.name || 'Untitled'}</h4>
                  <p style={styles.productPrice}>${((product.price || 0) / 100).toFixed(2)}</p>
                  <p style={styles.productType}>{product.type || 'product'}</p>
                </div>
                <button
                  onClick={() => toggleFeatured(product.id, product.featured)}
                  style={styles.removeButton}
                >
                  Remove from Featured
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>
          All Products ({nonFeaturedProducts.length}{hasMore ? '+' : ''})
        </h3>
        <div style={styles.productGrid}>
          {nonFeaturedProducts.map(product => (
            <div key={product.id} style={styles.productCard}>
              <img
                src={getProductImage(product)}
                alt={product.name || 'Product'}
                style={styles.productImage}
                onError={(e) => {
                  e.target.src = '/images/placeholder.jpg';
                }}
              />
              <div style={styles.productInfo}>
                <h4 style={styles.productName}>{product.name || 'Untitled'}</h4>
                <p style={styles.productPrice}>${((product.price || 0) / 100).toFixed(2)}</p>
                <p style={styles.productType}>{product.type || 'product'}</p>
              </div>
              <button
                onClick={() => toggleFeatured(product.id, product.featured)}
                style={styles.addButton}
              >
                Add to Featured
              </button>
            </div>
          ))}
        </div>

        {hasMore && (
          <div style={{ textAlign: 'center', marginTop: '30px' }}>
            <button
              onClick={loadMore}
              disabled={loadingMore}
              style={styles.loadMoreButton}
            >
              {loadingMore ? 'Loading...' : 'Show More'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    padding: '20px',
    maxWidth: '1400px',
    margin: '0 auto',
    backgroundColor: '#0a0a0a',
    minHeight: '100vh',
    color: '#fff'
  },
  title: {
    fontSize: '28px',
    marginBottom: '8px',
    color: '#ce0021'
  },
  subtitle: {
    fontSize: '14px',
    color: '#999',
    marginBottom: '30px'
  },
  loading: {
    textAlign: 'center',
    padding: '40px',
    fontSize: '18px',
    color: '#fff'
  },
  error: {
    backgroundColor: 'rgba(206, 0, 33, 0.2)',
    color: '#ff6b7a',
    padding: '12px',
    borderRadius: '6px',
    marginBottom: '20px',
    border: '1px solid rgba(206, 0, 33, 0.4)'
  },
  success: {
    backgroundColor: 'rgba(0, 206, 33, 0.2)',
    color: '#6bff7a',
    padding: '12px',
    borderRadius: '6px',
    marginBottom: '20px',
    border: '1px solid rgba(0, 206, 33, 0.4)'
  },
  section: {
    marginBottom: '40px'
  },
  sectionTitle: {
    fontSize: '20px',
    marginBottom: '20px',
    color: '#fff',
    borderBottom: '2px solid #ce0021',
    paddingBottom: '10px'
  },
  productGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: '20px'
  },
  emptyMessage: {
    textAlign: 'center',
    color: '#666',
    padding: '40px',
    fontSize: '16px'
  },
  productCard: {
    backgroundColor: '#1a1a1a',
    border: '1px solid #333',
    borderRadius: '8px',
    padding: '15px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    transition: 'transform 0.2s'
  },
  productImage: {
    width: '100%',
    height: '180px',
    objectFit: 'cover',
    borderRadius: '6px',
    marginBottom: '12px'
  },
  productInfo: {
    textAlign: 'center',
    marginBottom: '12px',
    width: '100%'
  },
  productName: {
    fontSize: '16px',
    fontWeight: '600',
    margin: '0 0 8px 0',
    color: '#fff'
  },
  productPrice: {
    fontSize: '14px',
    color: '#ce0021',
    margin: '0 0 4px 0',
    fontWeight: 'bold'
  },
  productType: {
    fontSize: '12px',
    color: '#999',
    margin: 0,
    textTransform: 'capitalize'
  },
  addButton: {
    width: '100%',
    padding: '10px',
    backgroundColor: '#ce0021',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold',
    transition: 'background-color 0.2s'
  },
  removeButton: {
    width: '100%',
    padding: '10px',
    backgroundColor: '#444',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold',
    transition: 'background-color 0.2s'
  },
  loadMoreButton: {
    padding: '12px 40px',
    backgroundColor: '#ce0021',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 'bold'
  },
  filterBar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
    marginBottom: '30px',
  },
  filterButton: {
    padding: '8px 18px',
    backgroundColor: '#1a1a1a',
    color: '#999',
    border: '1px solid #333',
    borderRadius: '20px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '500',
    transition: 'all 0.2s',
  },
  filterButtonActive: {
    backgroundColor: '#ce0021',
    color: '#fff',
    border: '1px solid #ce0021',
  }
};